import React from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import Scoreboard from "../match-scoreboard/scoreboard";
import { getMatchPageSectionSx } from "./matchPageSectionStyles";

function getFallbackTeamName(matchData, teamKey, fallback) {
  const team = Array.isArray(matchData?.[teamKey]) ? matchData[teamKey] : [];
  const leadPlayer = team[0];
  return leadPlayer?.nickname || leadPlayer?.name || fallback;
}

export default function MatchScoreboardSection({
  scoreboardData,
  matchData,
  lastTeam1Score,
  lastTeam2Score,
  isLive,
}) {
  const theme = useTheme();
  const sx = getMatchPageSectionSx(theme);
  const team1 = Array.isArray(scoreboardData?.team1) ? scoreboardData.team1 : [];
  const team2 = Array.isArray(scoreboardData?.team2) ? scoreboardData.team2 : [];
  const hasScoreboardData = team1.length > 0 || team2.length > 0;

  const team1Name = team1[0]?.name || getFallbackTeamName(matchData, "team1", "Team 1");
  const team2Name = team2[0]?.name || getFallbackTeamName(matchData, "team2", "Team 2");

  return (
    <Box id="scoreboard" sx={sx.sectionPanel}>
      <Typography sx={sx.sectionEyebrow}>Match Scoreboard</Typography>
      <Typography sx={sx.sectionTitle}>Round-by-round breakdown</Typography>
      <Typography sx={sx.sectionBody}>
        Round-by-round stats appear here once scoreboard data is available.
      </Typography>

      {!hasScoreboardData ? (
        <Box sx={{ ...sx.emptyState, mt: 2.5 }}>
          <Typography sx={{ fontWeight: 700, color: "#ffffff", mb: 0.6 }}>
            {isLive ? "Live scoreboard is warming up." : "Scoreboard data is not ready yet."}
          </Typography>
          <Typography sx={sx.sectionBody}>
            {isLive
              ? "Waiting for the first completed round to populate the player table."
              : "This match does not have completed round-end scoreboard data yet."}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3} sx={{ mt: 2.5 }}>
          {[
            {
              key: "team1",
              name: team1Name,
              score: lastTeam1Score,
              players: team1,
              won: !isLive && lastTeam1Score > lastTeam2Score,
            },
            {
              key: "team2",
              name: team2Name,
              score: lastTeam2Score,
              players: team2,
              won: !isLive && lastTeam2Score > lastTeam1Score,
            },
          ].map((team) => (
            <Box key={team.key} sx={sx.teamCard}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <Typography sx={{ color: "#ffffff", fontSize: "1.05rem", fontWeight: 700 }}>
                  Team_{team.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  {!isLive ? (
                    <Typography
                      sx={{
                        color: team.won ? "#57d476" : "#ff5a67",
                        fontSize: "0.82rem",
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      {team.won ? "Win" : "Loss"}
                    </Typography>
                  ) : null}
                  <Typography sx={{ color: "#ffffff", fontSize: "0.95rem", fontWeight: 700 }}>
                    Score: {team.score}
                  </Typography>
                </Stack>
              </Stack>

              <Scoreboard players={team.players} />
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}

MatchScoreboardSection.propTypes = {
  scoreboardData: PropTypes.object,
  matchData: PropTypes.object,
  lastTeam1Score: PropTypes.number,
  lastTeam2Score: PropTypes.number,
  isLive: PropTypes.bool,
};
